import { useCallback, useMemo, useState } from "react";
import { Box, styled, Paper, Stack } from "@mui/material";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import CardMemoirEmbed from "components/core/CardMemoirEmbed";
import MapToolbarCollapse from "components/map/MapToolbarCollapse";
import TripLocationSearch from "components/map/TripLocationSearch";
import TripStopCard from "components/map/TripStopCard";
import { TripEmissionDisclaimerInfoButton } from "components/trips/TripEmissionDetails";
import TripRainforestTreeWidget from "components/trips/TripRainforestTreeWidget";
import useTripPlannerStops from "hooks/maps/useTripPlannerStops";
import useIsMobile from "hooks/base-app/useIsMobile";
import { kgToTrees, metersToKm } from "libraries/emission";
import { sumPlaceActivityEmissionKg } from "libraries/placeTypeEmissions";
import { avoidedCarEmissionKg } from "libraries/legEmissions";
import { ROUTE } from "components/base-app/AppRoutes";
import {
  selectReferenceCardMarker,
  selectReferenceCar,
  selectReferenceItinerary,
  selectReferenceTrip,
  selectReferenceTripEmission,
  selectReferenceElectricGridCountry,
  selectHasReferenceTrip,
  clearItinerary,
  clearTrip,
} from "redux/slices/shed/shedSlice";

const PANEL_MAX_WIDTH = 408;

const PaperPanel = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "minimized",
})<{ minimized?: boolean }>(({ theme, minimized }) => ({
  width: "100%",
  maxWidth: PANEL_MAX_WIDTH,
  minWidth: 0,
  minHeight: 0,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: minimized ? "auto" : "100%",
  maxHeight: minimized ? "none" : "100%",
  flexShrink: minimized ? 1 : 0,
  flexGrow: minimized ? 0 : undefined,
  borderRadius: 0,
  padding: theme.spacing(1.5, 1.25),
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    height: "auto",
    maxHeight: minimized ? "none" : "55vh",
    flexShrink: 1,
    flexGrow: 0,
  },
  [theme.breakpoints.up("sm")]: {
    width: PANEL_MAX_WIDTH,
  },
}));

type Props = {
  shareRoute?: string;
  includeExperience?: boolean;
};

const TripPlannerPanel = ({
  shareRoute = ROUTE.MAP,
  includeExperience = false,
}: Props) => {
  const [isMinimized, setIsMinimized] = useState(false);

  const cardMarker = useAppSelector(selectReferenceCardMarker);
  const referenceCar = useAppSelector(selectReferenceCar);
  const referenceTrip = useAppSelector(selectReferenceTrip);
  const itinerary = useAppSelector(selectReferenceItinerary);
  const emission = useAppSelector(selectReferenceTripEmission);
  const gridCountry = useAppSelector(selectReferenceElectricGridCountry);
  const hasTrip = useAppSelector(selectHasReferenceTrip);
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();

  const {
    stops,
    canAddStop,
    handleAddStop,
    handleRemoveStop,
    handleReorderStops,
    handleSetLegMode,
  } = useTripPlannerStops();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const canReorder = stops.length > 1;

  const itineraryMeters = itinerary?.distanceMeters ?? 0;
  const flightMeters = itinerary?.flightDistanceMeters ?? 0;
  const flightKg = itinerary?.flightKgEmission ?? 0;
  const walkMeters = itinerary?.walkDistanceMeters ?? 0;
  const cycleMeters = itinerary?.cycleDistanceMeters ?? 0;
  const trainMeters = itinerary?.trainDistanceMeters ?? 0;
  const trainKg = itinerary?.trainKgEmission ?? 0;
  const walkBikeAvoidedKg = avoidedCarEmissionKg(
    walkMeters + cycleMeters,
    referenceCar,
    gridCountry
  );
  const activityKg = useMemo(
    () => sumPlaceActivityEmissionKg(stops.map((stop) => stop.location)),
    [stops]
  );
  const extraTransportKg = flightKg + trainKg + walkBikeAvoidedKg;
  const treeCount =
    (emission.totalTrees + kgToTrees(activityKg + extraTransportKg)) *
    (referenceTrip.commuteFrequency || 1);
  const distanceKm = metersToKm(
    itineraryMeters * (referenceTrip.commuteFrequency || 1)
  );
  const flightKm = metersToKm(
    flightMeters * (referenceTrip.commuteFrequency || 1)
  );
  const walkKm = metersToKm(walkMeters * (referenceTrip.commuteFrequency || 1));
  const cycleKm = metersToKm(
    cycleMeters * (referenceTrip.commuteFrequency || 1)
  );
  const trainKm = metersToKm(
    trainMeters * (referenceTrip.commuteFrequency || 1)
  );
  const distanceCaption = [
    distanceKm > 0 ? `${Math.round(distanceKm)} km drive` : null,
    walkKm > 0 ? `${Math.round(walkKm)} km walk` : null,
    cycleKm > 0 ? `${Math.round(cycleKm)} km bike` : null,
    trainKm > 0 ? `${Math.round(trainKm)} km transit` : null,
    flightKm > 0 ? `${Math.round(flightKm)} km flight` : null,
  ]
    .filter(Boolean)
    .join(" · ") || undefined;

  const displayedTrip = useMemo(
    () => ({
      ...referenceTrip,
      distanceMeters: itineraryMeters,
    }),
    [referenceTrip, itineraryMeters]
  );

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  const handleClearTrip = useCallback(() => {
    setIsMinimized(false);
    dispatch(clearTrip());
    dispatch(clearItinerary());
  }, [dispatch]);

  const handleDragStart = useCallback((index: number) => {
    setDragIndex(index);
    setDropIndex(index);
  }, []);

  const handleDragOver = useCallback((index: number) => {
    setDropIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDropIndex(null);
  }, []);

  const handleDrop = useCallback(
    (toIndex: number) => {
      if (dragIndex !== null) {
        handleReorderStops(dragIndex, toIndex);
      }
      setDragIndex(null);
      setDropIndex(null);
    },
    [dragIndex, handleReorderStops]
  );

  return (
    <PaperPanel elevation={3} minimized={isMinimized}>
      <MapToolbarCollapse
        onClearTrip={handleClearTrip}
        isMinimized={isMinimized}
        onToggle={toggleMinimize}
        shareRoute={shareRoute}
        includeExperience={includeExperience}
      >
        <Stack width="100%" gap={1.25} mt={0.5} sx={{ pb: 0.5 }}>
          <TripLocationSearch
            disabled={!canAddStop}
            onAdd={handleAddStop}
          />

          {stops.length > 0 && (
            <Stack gap={0.75} width="100%">
              {stops.map((stop, index) => (
                <TripStopCard
                  key={stop.id}
                  stop={stop}
                  index={index}
                  onRemove={handleRemoveStop}
                  draggable={canReorder}
                  isDragging={dragIndex === index}
                  isDropTarget={
                    dragIndex !== null &&
                    dropIndex === index &&
                    dragIndex !== index
                  }
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDrop={handleDrop}
                  onLegModeChange={handleSetLegMode}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </MapToolbarCollapse>

      {hasTrip && (
        <Box pt={1.25} flexShrink={0}>
          <TripRainforestTreeWidget
            treeCount={treeCount}
            distanceKm={distanceKm}
            distanceCaption={distanceCaption}
            showCarControls
            topRightAction={
              <TripEmissionDisclaimerInfoButton
                trip={displayedTrip}
                tripCar={referenceCar}
                activityKgEmission={activityKg}
                flightKgEmission={flightKg}
                extraTransportKgEmission={trainKg + walkBikeAvoidedKg}
              />
            }
          />
        </Box>
      )}

      {!isMobile && !isMinimized && (
        <Box mt={1} display="flex" justifyContent="center" flexShrink={0}>
          <CardMemoirEmbed cardId={cardMarker?.cardId} />
        </Box>
      )}
    </PaperPanel>
  );
};

export default TripPlannerPanel;
